import { useContext, useEffect, useMemo, useState } from "react";

import { type ApolloContextValue, getApolloContext } from "@apollo/client";
import { ProductTargetingFeDisplayDataType } from "@intelligems/ig-types/fe-only/experience-product-targeting";
import { chunkArray } from "@intelligems/ig-utils/common";
import { fromGqlId, toGqlId } from "@src/api/formatters/shopify";
import { ProductVariantItem } from "@src/features/sharedExperience/components/SharedProductVariantTable";
import {
  FetchProductsByIdsDocument,
  FetchProductsByIdsQuery,
} from "@src/generated";

type ProductNode = Extract<
  FetchProductsByIdsQuery["nodes"][number],
  { __typename?: "Product" }
>;

const SHOPIFY_ID_LIMIT = 250;

export const useProductsByIds = (
  productIds: string[],
  displayData?: Record<string, ProductTargetingFeDisplayDataType> | null
) => {
  // The share bundle ships without an Apollo client, and useApolloClient throws
  // when there is none.
  const { client } = useContext<ApolloContextValue>(getApolloContext());
  const [fetchedNodes, setFetchedNodes] = useState<ProductNode[]>([]);
  const [loading, setLoading] = useState(false);

  // Convert productIds to gqlIds and identify which ones need fetching
  const { gqlIds, idsToFetch } = useMemo(() => {
    const allGqlIds = productIds
      .filter((id) => id && id.trim() !== "")
      .map((id) => toGqlId(id, "Product"));

    // Only fetch IDs we don't have display data for
    const needsFetch = allGqlIds.filter((gqlId) => {
      const numericId = fromGqlId(gqlId);
      return (
        !displayData?.[numericId]?.title ||
        displayData?.[numericId]?.title === "" ||
        !displayData?.[numericId]?.image
      );
    });

    return { gqlIds: allGqlIds, idsToFetch: needsFetch };
  }, [productIds, displayData]);

  useEffect(() => {
    if (!client || idsToFetch.length === 0) {
      setFetchedNodes([]);
      return;
    }

    const fetchAllChunks = async () => {
      setLoading(true);
      try {
        const chunks = chunkArray(idsToFetch, SHOPIFY_ID_LIMIT);
        const results = await Promise.all(
          chunks.map(async (chunk) =>
            client.query<FetchProductsByIdsQuery>({
              query: FetchProductsByIdsDocument,
              variables: { ids: chunk },
              fetchPolicy: "cache-first",
            })
          )
        );
        const allNodes = results.flatMap(
          (r) => (r.data?.nodes?.filter(Boolean) as ProductNode[]) ?? []
        );
        setFetchedNodes(allNodes);
      } catch (error) {
        console.error("Error fetching products by IDs:", error);
        setFetchedNodes([]);
      } finally {
        setLoading(false);
      }
    };

    void fetchAllChunks();
  }, [idsToFetch, client]);

  // Build display data from fetched nodes for Redux persistence
  const fetchedDisplayData = useMemo<Record<
    string,
    ProductTargetingFeDisplayDataType
  > | null>(() => {
    if (fetchedNodes.length === 0) {
      return null;
    }

    const data: Record<string, ProductTargetingFeDisplayDataType> = {};
    fetchedNodes.forEach((node) => {
      const numericId = fromGqlId(node.id);
      const title = node.title ?? "Unknown";
      const image =
        node.featuredMedia?.__typename === "MediaImage"
          ? node.featuredMedia?.image?.url
          : undefined;

      data[numericId] = { title, image };
    });
    return data;
  }, [fetchedNodes]);

  // Numeric product id -> { title, handle } for every fetched product. Used to
  // re-hydrate analytics product filters restored from the URL, which carry
  // only the numeric id to prevent analytics url from crashing.
  const productDataById = useMemo<
    Record<string, { title: string; handle: string }>
  >(() => {
    const data: Record<string, { title: string; handle: string }> = {};
    fetchedNodes.forEach((node) => {
      data[fromGqlId(node.id)] = {
        title: node.title ?? "Unknown",
        handle: node.handle,
      };
    });
    return data;
  }, [fetchedNodes]);

  const items = useMemo<ProductVariantItem[]>(() => {
    // Build items from both displayData and fetched results
    return gqlIds.map((gqlId) => {
      const numericId = fromGqlId(gqlId);

      // First check if we have display data for this product
      if (displayData?.[numericId]) {
        return {
          productId: gqlId,
          title: displayData[numericId].title || "Default title",
          image: displayData[numericId].image,
          variantId: undefined,
        };
      }

      // Otherwise, look for it in the fetched results
      const fetchedNode = fetchedNodes.find(
        (node): node is ProductNode =>
          node?.__typename === "Product" && node.id === gqlId
      );

      if (fetchedNode) {
        return {
          productId: fetchedNode.id,
          title: fetchedNode.title ?? "Unknown",
          image:
            fetchedNode.featuredMedia?.__typename === "MediaImage"
              ? (fetchedNode.featuredMedia?.image?.url ?? undefined)
              : undefined,
          variantId: undefined,
        };
      }

      // Fallback if not found anywhere
      return {
        productId: gqlId,
        title: "Loading...",
        image: undefined,
        variantId: undefined,
      };
    });
  }, [gqlIds, displayData, fetchedNodes]);

  return {
    items,
    loading,
    fetchedDisplayData,
    productDataById,
  };
};
